import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { FeaturePermission } from '../../admin/users/store';
import { getUserHasFeaturePermission } from '../../auth/login/store';
import { Icon } from '../../shared/icon/Icon';
import { SaveFile, WarningSign } from '../../shared/icons';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { Spinner } from '../../shared/spinner/Spinner';
import { Wizard } from '../../shared/wizard/Wizard';
import { ConfigureFields, DocumentSelect, MetaFields, ReportFilter } from './report-tabs';
import { DocumentReportFields } from './report-tabs/DocumentReportFields';
import styles from './Reports.module.scss';
import { ReportPage, ReportTab, getAllReportFields, getBackConfirmationModalOpen, getIsGeneratingReport, getReportOpenFieldSections, getReportUpdated, getSelectedDocumentNameIds, getSelectedTab, getSelectedTemplate, getSelectedTemplateId, removeAllFieldSections, resetReport, saveReportTemplateStarted, setReportPage, setSelectedTab, toggleConfirmationModal, toggleGenerateModalOpen, toggleSaveTemplateModalOpen } from './store';
import { SaveDocumentTemplateModal } from './templates/SaveDocumentTemplateModal';
import { GenerateDocumentReportModal } from './GenerateDocumentReportModal';

export const DocumentReportsWizard: React.FC = () => {
    const dispatch = useAppDispatch();

    const testId = 'generate-document-report';

    const selectedTab = useAppSelector(getSelectedTab);
    const documentNameIds = useAppSelector(getSelectedDocumentNameIds);
    const reportFields = useAppSelector(getAllReportFields);
    const backConfirmationOpen = useAppSelector(getBackConfirmationModalOpen);
    const reportUpdated = useAppSelector(getReportUpdated);
    const selectedTemplate = useAppSelector(getSelectedTemplate);
    const selectedTemplateId = useAppSelector(getSelectedTemplateId);
    const openFieldSections = useAppSelector(getReportOpenFieldSections);
    const isGeneratingReport = useAppSelector(getIsGeneratingReport);

    const reportTabs = Object.values(ReportTab);
    const selectedTabIndex = reportTabs.indexOf(selectedTab);
    const returnPage = selectedTemplateId ? ReportPage.TEMPLATES : ReportPage.SELECT;
    const hasEditDocumentReportPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.EDIT_DOCUMENT_REPORT_TEMPLATES]));
    const hasGenerateDocumentReportPermission = useAppSelector(getUserHasFeaturePermission([FeaturePermission.GENERATE_DOCUMENT_REPORTS]));

    const canMakeTemplateChanges = hasEditDocumentReportPermission;

    const selectTab = useCallback((tab: ReportTab) => dispatch(setSelectedTab(tab)), [dispatch]);
    const previous = useCallback(() => selectTab(reportTabs[selectedTabIndex - 1]), [reportTabs, selectTab, selectedTabIndex]);
    const next = useCallback(() => selectTab(reportTabs[selectedTabIndex + 1]), [reportTabs, selectTab, selectedTabIndex]);
    const generate = useCallback(() => dispatch(toggleGenerateModalOpen(true)), [dispatch]);
    const goBack = useCallback(() => {
        dispatch(setReportPage(returnPage));
        dispatch(resetReport(returnPage));
    }, [dispatch, returnPage]);

    const toggleBackConfirmationModal = useCallback((value: boolean) => dispatch(toggleConfirmationModal(value)), [dispatch]);
    const onBackClick = useCallback(() => reportUpdated ? toggleBackConfirmationModal(true) : goBack(), [toggleBackConfirmationModal, goBack, reportUpdated]);
    const openSaveModal = useCallback(() => dispatch(toggleSaveTemplateModalOpen(true)), [dispatch]);

    const saveTemplate = useCallback(() => dispatch(saveReportTemplateStarted()), [dispatch]);
    const collapseAllOpenFieldSections = useCallback(() => dispatch(removeAllFieldSections()), [dispatch]);
    const firstTabIncomplete = useMemo(() => documentNameIds.length === 0, [documentNameIds]);

    const wizardTabs = useMemo(() => reportTabs.map(tab => ({
        label: tab,
        selected: tab === selectedTab,
        onClick: () => selectTab(tab),
        disabled: tab !== ReportTab.SELECT && firstTabIncomplete
    })), [selectTab, selectedTab, reportTabs, firstTabIncomplete]);

    const everyAgreementTypeHasReportFields = useMemo(() => Object.values(reportFields).every(agreementTypeReportFields => agreementTypeReportFields.length > 0), [reportFields]);

    const saveAndGenerateDisabled = useMemo(() => firstTabIncomplete || !everyAgreementTypeHasReportFields || !hasGenerateDocumentReportPermission || isGeneratingReport, [firstTabIncomplete, everyAgreementTypeHasReportFields, hasGenerateDocumentReportPermission, isGeneratingReport]);

    const saveAndGenerateDisabledTooltip = useCallback((action: string) => {
        const disabledArray = [];
        if (documentNameIds.length === 0) {
            disabledArray.push(`You must select at least one document before you can ${action} a report template`);
        }
        if (!everyAgreementTypeHasReportFields) {
            disabledArray.push(`You must select at least one field for each included agreement type before you can ${action} a report template`);
        }
        if (!hasGenerateDocumentReportPermission && action === 'generate') {
            disabledArray.push('You are not permitted to generate a report');
        }
        return disabledArray;
    }, [documentNameIds, everyAgreementTypeHasReportFields, hasGenerateDocumentReportPermission]);

    const showSaveButton = (isNull(selectedTemplate) || reportUpdated) && canMakeTemplateChanges;
    const showSaveExistingButton = (!isNull(selectedTemplate) && !!reportUpdated) && canMakeTemplateChanges;

    const showCollapseAllButton = useMemo(() => openFieldSections.length > 0 && selectedTab === ReportTab.FIELDS, [openFieldSections, selectedTab]);

    const saveLabel = `Save${selectedTemplate ? ' As' : ''}`;

    const wizardButtons = useMemo(() => [
        {
            label: 'Back',
            onClick: onBackClick,
            showButton: true,
            displayLeft: true,
            disabled: false,
            disabledTooltip: null
        },
        {
            label: saveLabel,
            onClick: openSaveModal,
            disabled: saveAndGenerateDisabled,
            disabledTooltip: saveAndGenerateDisabledTooltip('save'),
            showButton: showSaveButton,
            displayLeft: true
        },
        {
            label: 'Save',
            onClick: saveTemplate,
            disabled: saveAndGenerateDisabled,
            showButton: showSaveExistingButton,
            displayLeft: true,
            disabledTooltip: null
        },
        {
            label: 'Collapse All',
            onClick: collapseAllOpenFieldSections,
            disabled: false,
            showButton: showCollapseAllButton,
            displayLeft: true,
            disabledTooltip: null
        },
        {
            label: 'Previous',
            onClick: previous,
            disabled: selectedTabIndex === 0,
            disabledTooltip: null,
            showButton: selectedTabIndex !== 0
        },
        {
            label: 'Next',
            onClick: next,
            disabled: firstTabIncomplete,
            disabledTooltip: null,
            showButton: selectedTabIndex !== reportTabs.length - 1
        },
        {
            label: 'Generate',
            onClick: generate,
            disabled: saveAndGenerateDisabled,
            disabledTooltip: saveAndGenerateDisabledTooltip('generate'),
            showButton: selectedTabIndex === reportTabs.length - 1
        }
    ], [onBackClick, previous, next, generate, reportTabs, selectedTabIndex, saveAndGenerateDisabled, saveAndGenerateDisabledTooltip, openSaveModal, showSaveButton, saveTemplate, showSaveExistingButton, saveLabel, firstTabIncomplete, collapseAllOpenFieldSections, showCollapseAllButton]);

    const reportContent = useMemo(() => {
        switch (selectedTab) {
            case ReportTab.SELECT:
                return <DocumentSelect />;
            case ReportTab.META_FIELDS:
                return <MetaFields />;
            case ReportTab.FIELDS:
                return <DocumentReportFields />;
            case ReportTab.CONFIGURE:
                return <ConfigureFields />;
            case ReportTab.FILTER:
                return <ReportFilter />;
            default:
                return <Spinner />;
        }
    }, [selectedTab]);

    return (
        <div className={styles.wizardWrapper} data-testid={`${testId}-wrapper`}>
            <Wizard
                buttons={wizardButtons}
                tabs={wizardTabs}
                title='Generate a Report'
                testId={testId}
            >
                <div className={styles.contentWrapper}>
                    {reportContent}
                </div>
            </Wizard>
            <ConfirmationModal
                isOpen={backConfirmationOpen}
                closeModal={() => toggleBackConfirmationModal(false)}
                confirm={goBack}
                confirmLabel='Yes'
                closeLabel='Cancel'
                testId='document-report-back'
            >
                <div className={styles.backModalWrapper}>
                    <ModalHeader
                        testId='unsaved-changes-modal'
                        label='Unsaved Changes'
                        icon={SaveFile}
                    />
                    <div className={styles.backModalWarning}>
                        <Icon icon={WarningSign} />
                        <div data-testid='unsaved-changes-warning-header' className={styles.backModalWarningTitle}>You have unsaved changes.</div>
                    </div>
                    <div data-testid='unsaved-changes-warning-content' className={styles.backModalContent}>Are you sure you want to go back without saving?</div>
                </div>
            </ConfirmationModal>
            <SaveDocumentTemplateModal />
            <GenerateDocumentReportModal />
        </div>
    );
};
